import type { Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { CustomerModel } from 'src/app/models/customer-model';

import {
  ILoginRequestData,
  ILoginResponse,
  ISendLoginCodeRequestObj,
  IValidateLoginCodeRequestData,
  IValidateLoginCodeRequestObj,
  IValidateLoginCodeResponse,
  IValidatePasswordResetCodeRequestData,
  IValidatePasswordResetCodeRequestObj
} from '../../interfaces/authentication.interface';

import { isUndefined } from '../codeUtils';
import { UserSessionService } from '../user-session/user-session.service';
import { WebservicesService } from '../webservices/webservices.service';
import { AppsflyerService } from '../marketing/appsflyer.service';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { ConfigService } from '../config/config.service';
import { FacebookTracker } from '../marketing/facebook-tracker';
import { API_LIB } from 'src/library/api.lib';

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  public customer: CustomerModel;

  constructor(
    private webservice: WebservicesService,
    private userSession: UserSessionService,
    private configService: ConfigService,
    public appsFlyerService: AppsflyerService,
    private facebookTracker: FacebookTracker
  ) {
    this.userSession.getCustomer().subscribe((customer) => {
      this.customer = customer;
    });
  }

  callLogin(data: ILoginRequestData): Subject<ILoginResponse> {
    let advertisingId = this.facebookTracker.getAdvertisingIDs();
    data.advertiseId = advertisingId.IDFA;
    if (!data.advertiseId) delete data.advertiseId;

    let trackingData = this.appsFlyerService.getTrackingIDs();
    data.deviceDetails = trackingData.deviceDetails;
    data.externalSource = trackingData.externalSource;
    if (!data.device_id) delete data.device_id;
    if (!data.deviceDetails) delete data.deviceDetails;
    if (!data.externalSource) delete data.externalSource;

    return this.webservice.post(API_LIB.AUTHENTICATION_LOGIN, data);
  }


  callPin(data) {
    const obj: any = {
      customer_id: data.customerId,
      session_token: this.userSession.sessionToken
    }
    obj.pin = data.pinNumber;

    return this.webservice.post('authentication/pin', obj);
  }


  getCsrfToken() {
    return this.webservice.get(API_LIB.EXTERNAL_CSRF_TOKEN);
  }

  async checkSession() {
    const sessionToken = this.userSession.getSessionToken();
    const customer: CustomerModel = JSON.parse(
      localStorage.getItem("userSession.customer")
    );
    const customerId = customer?.customerId;

    const obj: any = {
      customer_id: customerId,
      session_token: sessionToken,
    };
    //return no subject if there are no sessionToken or customerId, so the client doesn't make a webservice call
    return {
      subject:
        !sessionToken || !customerId
          ? null
          : this.webservice.get("authentication/check-session", obj),
      sessionToken: sessionToken,
    };
  }

  callVerifyKYC(data) {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
    };
    if (data.government_id != null && data.government_id != "") {
      obj.governmentId = data.government_id;
      obj.governmentTypeId = "ssn";
    }
    if (!obj.session_token || !obj.customer_id || !obj.governmentId) return;
    return this.webservice.post("authentication/verify-kyc", obj);
  }

  callSignupStep1(data) {
    if (data.promoCode != null && data.promoCode != "") {
      data.bonusCode = data.promoCode;
    }
    const obj: any = {
      csrf: data.csrf,
      clientapp: data.clientapp,
      email: data.emailGroup.email,
      password: data.passwordGroup.password,
      //extAffiliateId: data.extAffiliateId,
      //customerAttributionData: data.customerAttributionData,
      certOver18: data.certOver18,
      promo_code: data.promo_code,
      allowPromoEmails: data.allowPromoEmails,
      //behalf: data.behalf,
    };

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_PROMO_FIELD === '1') {
      obj.allowPromoEmails = data.allowPromoEmails
    }
    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_BEHALF_FIELD === '1') {
      obj.behalf = data.behalf
    }

    if (this.configService.getConfig().FEATURE_PHONE_ON_STEP_ONE === '1') {
      obj.phoneNumber = data.phoneNumber;
    }
    if (document.referrer.split("/")[2] != null && document.referrer.split("/")[2] != "" && !isUndefined(document.referrer.split("/")[2])) {
      obj.landingpage_source = document.referrer.split("/")[2];
    }
    return this.webservice.post(API_LIB.AUTHENTICATION_SIGNUP_STEP_ONE, obj);
  }

  callSignupStep2(data, migratedFlag?) {
    const month =
      ~~data.dateOfBirthGroup.dateOfBirthMonth < 10
        ? "0" + data.dateOfBirthGroup.dateOfBirthMonth
        : data.dateOfBirthGroup.dateOfBirthMonth;

    const dob = new Date(
      data.dateOfBirthGroup.dateOfBirthYear +
      "-" +
      month +
      "-" +
      data.dateOfBirthGroup.dateOfBirthDay
    );

    const siteCountryRegion = this.configService.getConfig().FEATURE_SITE_COUNTRY_REGION ? this.configService.getConfig().FEATURE_SITE_COUNTRY_REGION : 'USA';

    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,

      csrf: data.csrf,

      clientapp: data.clientapp,
      extAffiliateId: data.extAffiliateId,
      customerAttributionData: data.customerAttributionData,

      salutation: !!data.salutation ? data.salutation : "Mr",
      firstName: data.firstName,
      lastName: data.lastName,

      //DOB year-month-day

      birthDate: dob.toISOString().split("T")[0],

      street: data.street,
      city: data.city,
      country: data.country,
      province: data.province,
      postalCode: data.countryInfoGroup.postalCode,
      phoneNumber: data.phoneNumber,

      securityQuestion: data.securityQuestion,
      securityAnswer: data.securityAnswer,
      secondSecurityQuestion: data.secondSecurityQuestion,
      secondSecurityAnswer: data.secondSecurityAnswer,
      certOver18: data.certOver18,
    };

    if (migratedFlag) {
      obj.promo_code = data.promo_code;
    }

    //we should use below customProperty check for MIGRATION field
    if (this.customer.getCustomDataProperty('CRE_BY') === 'MIGRATION' || this.customer.getCustomDataProperty('CRE_BY') === 'PA_MIGRATION') {
      obj.withTOC = true;
    }

    if (data.middleName != null && data.middleName != "") {
      obj.middleName = data.middleName;
    }

    if (data.street2 != null && data.street2 != "") {
      obj.street2 = data.street2;
    }

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_ALIAS_FIELD === '1') {
      obj.nickName = data.aliasName;
    }

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_CITIZEN_FIELD === '1') {
      obj.citizenship = data.citizenship;
    }

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_JOB_INDUSTRY_FIELD === '1') {
      obj.jobTitle = data.descriptiveOccupation.jobTitle;
      obj.jobIndustry = data.descriptiveOccupation.industry;
    }

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_EMPLOYER_NAME_FIELD === '1') {
      obj.employerName = data.employerName;
    }

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_EMPLOYER_ADDRESS_FIELD === '1') {
      obj.employerAddress = data.employerAddress;
    }

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_BUSINESS_PHONE_FIELD === '1') {
      obj.businessPhoneNumber = data.businessPhoneNumber;
    }

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_SSN_FIELD === '1') {
      obj.governmentId = data.governmentId;
      obj.governmentTypeId = "ssn";
    }

    if (siteCountryRegion !== 'USA') {
      obj.isPEPOrHIO = data.ackRegulationsCan5;
      obj.allowPromoEmails = data.ackRegulationsCan2;
      obj.allowPromoSms = data.ackRegulationsCan3;
    } else {
      obj.sendEmailUponLogin = data.ackRegulationsEmail;
      obj.allowPromoSms = data.allowPromoSms;
      obj.allowPromoEmails = data.allowPromoEmails;
      obj.allowPromoPost = data.allowPromoPost;
      obj.ackRegulations = data.ackRegulations;
    }


    return this.webservice.post(API_LIB.AUTHENTICATION_SIGNUP_STEP_TWO, obj);
  }

  callConfirmAuth(data) {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      confirmAuth: data.confirmAuth,
    };

    return this.webservice.post(API_LIB.AUTHENTICATION_UPDATE_ACCOUNT, obj);
  }

  callLogout(params) {
    const obj: any = {
      session_token: this.userSession.sessionToken,
    };
    if (
      !isUndefined(this.userSession.customer) &&
      this.userSession.customer !== null
    ) {
      obj.customer_id = this.userSession.customer.customerId;
    }
    if (params && "reason" in params) {
      obj.reason = params.reason;
    }
    return this.webservice.post(API_LIB.AUTHENTICATION_LOGOUT, obj);
  }

  getUniqueDeviceIdAction(data) {
    return this.webservice.get("external/get-unique-device-id", data);
  }

  callUpdateAccount(data) {
    const obj: any = {
      customer_id: this.customer.customerId,
      //email: data.email,
      //password: data.password,
      session_token: this.userSession.sessionToken,
      street: data.street,
      street2: !!data.street2 ? data.street2 : " ",
      city: data.city,
      country: data.countryInfoGroup.country,
      postalCode: data.countryInfoGroup.postalCode,
      province: data.province,
      phoneNumber: data.phoneNumber,
      sendEmailUponLogin: data.sendEmailUponLogin,
      allowPromoEmails: data.allowPromoEmails,
      allowPromoSms: data.allowPromoSms,
      clientapp: data.clientapp,
    };

    if (data.email != null && data.email != '') {
      obj.email = data.email
    }

    if (data.password != null && data.password != '') {
      obj.password = data.password
    }

    if (this.configService.getConfig().FEATURE_INFOPAGE_SHOW_JOB_INDUSTRY_FIELD === '1') {
      obj.jobTitle = data.jobTitle;
      obj.jobIndustry = data.industry;
    }

    if (this.configService.getConfig().FEATURE_INFOPAGE_SHOW_EMPLOYER_NAME_FIELD === '1') {
      obj.employerName = data.employerName;
    }

    if (this.configService.getConfig().FEATURE_INFOPAGE_SHOW_EMPLOYER_ADDRESS_FIELD === '1') {
      obj.employerAddress = data.employerAddress;
    }

    if (this.configService.getConfig().FEATURE_INFOPAGE_SHOW_BUSINESS_PHONE === '1') {
      obj.businessPhoneNumber = data.businessPhoneNumber;
    }

    //Annual Reconfirmation Data
    if (data.firstName != null && data.firstName != '') {
      obj.firstName = data.firstName
    }

    if (data.lastName != null && data.lastName != '') {
      obj.lastName = data.lastName
    }

    if (data.middleName != null && data.middleName != '') {
      obj.middleName = data.middleName
    }

    if (data.aliasName != null && data.aliasName != '') {
      obj.nickname = data.aliasName
    }

    return this.webservice.post(API_LIB.AUTHENTICATION_UPDATE_ACCOUNT, obj);
  }

  // Modified Step-1 Just for Loyalty Program Signup for the player who has loyalty card.
  callUpdateAccountForBoydCard(data) {
    if (data.promoCode != null && data.promoCode != "") {
      data.bonusCode = data.promoCode;
    }
    const obj: any = {
      csrf: data.csrf,
      clientapp: data.clientapp,
      session_token: this.userSession.getSessionToken(),
      customer_id: this.customer.customerId,
      email: data.emailGroup.email,
      password: data.passwordGroup.password,
      certOver18: data.certOver18,
      promo_code: data.promo_code,
      allowPromoEmails: data.allowPromoEmails,
    };

    //Pass TOC all the time to webservice for BoydCards Signup
    obj.withTOC = true

    if (document.referrer.split("/")[2] != "") {
      obj.landingpage_source = document.referrer.split("/")[2];
    }
    return this.webservice.post(API_LIB.AUTHENTICATION_UPDATE_ACCOUNT, obj);
  }

  callUpdatePromotionPreferences(data) {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      sendEmailUponLogin: data.sendEmailUponLogin,
      allowPromoEmails: data.allowPromoEmails,
      allowPromoSms: data.allowPromoSms,
      allowPromoPost: data.allowPromoPost
    };
    return this.webservice.post(API_LIB.AUTHENTICATION_UPDATE_ACCOUNT, obj);
  }

  callGetAccount() {
    const obj: any = {
      session_token: this.userSession.sessionToken,
    };

    return this.webservice.get("authentication/get-account", obj);
  }

  /*
        POST /authentication/change-password
    */
  callChangePassword(data) {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      current: data.currentPassword,
      password: data.passwordGroup.newPassword,
      q1: data?.securityGroup?.securityAnswer,
      q2: data?.securityGroup?.secondSecurityAnswer,
    };

    return this.webservice.post("authentication/change-password", obj);
  }

  callChangePasswordNoSec(data, email) {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      current: data.currentPassword,
      password: data.passwordGroup.newPassword,
      email: email
      // q1: data.securityGroup.securityAnswer,
      // q2: data.securityGroup.secondSecurityAnswer,
    };

    return this.webservice.post("authentication/change-password", obj);
  }

  /*
        POST /authentication/change-password
        Adjusted call for playerMigration feature/forcePasswordReset params
    */
  callForceChangePassword(data) {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      current: data.currentPassword,
      password: data.passwordGroup.newPassword,
    };
    return this.webservice.post("authentication/change-password", obj);
  }

  callSetSecurityQuestions(data) {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      securityQuestion: data.securityQuestion,
      securityAnswer: data.securityAnswer,
      clientapp: data.clientapp,
      secondSecurityQuestion: data.secondSecurityQuestion,
      secondSecurityAnswer: data.secondSecurityAnswer,
    };
    return this.webservice.post(API_LIB.AUTHENTICATION_UPDATE_ACCOUNT, obj);
  }

  updateCustomData(data) {
    data.customer_id = this.customer.customerId;
    data.session_token = this.userSession.sessionToken;
    return this.webservice.post("authentication/update-custom-data", data);
  }

  callForgotPassword(data) {
    return this.webservice.post("authentication/forgot-password", data);
  }

  sendLoginCode(customerId, channel = "SMS", phone?: string): Subject<PlatformResponse<void>> {
    const obj: ISendLoginCodeRequestObj = {
      customer_id: customerId,
      session_token: this.userSession.sessionToken,
      channel: isUndefined(this.customer.address?.phoneNumber) ? 'EMAIL' : channel,
    };
    if (phone) {
      obj.phone_number = phone;
    }
    return this.webservice.post("authentication/send-login-code", obj);
  }

  sendForgotPasswordLoginCode(customerId, channel = "SMS", phone?: string): Subject<PlatformResponse<void>> {
    const obj: any = {
      customer_id: customerId,
      channel: channel
    };
    if (phone) {
      obj.phone_number = phone;
    }
    return this.webservice.post("authentication/send-login-code", obj);
  }

  validateLoginCode(
    data: IValidateLoginCodeRequestData
  ): Subject<IValidateLoginCodeResponse> {
    const obj: IValidateLoginCodeRequestObj = {
      customer_id: data.customerId,
      session_token: this.userSession.sessionToken,
    };

    obj.pin = data.pinNumber;

    if (data.deviceId) {
      obj.deviceId = data.deviceId;
    }
    obj.mfa = true;

    if (data.target) {
      obj.target = data.target;
    } else {
      obj.target = 'sms';
    }
    return this.webservice.post("authentication/validate-login-code", obj);
  }

  validatePasswordResetCode(
    data: IValidatePasswordResetCodeRequestData
  ): Subject<IValidateLoginCodeResponse> {
    const obj: IValidatePasswordResetCodeRequestObj = {
      customer_id: data.customerId,
      session_token: this.userSession.sessionToken,
      resetPasswordToken: data.resetPasswordToken,
      pin: data.pinNumber
    };

    return this.webservice.post("authentication/validate-login-code", obj);
  }

  validateResetCode(
    data: IValidateLoginCodeRequestData
  ): Subject<IValidateLoginCodeResponse> {
    const obj: IValidateLoginCodeRequestObj = {
      customer_id: data.customerId,
      session_token: this.userSession.sessionToken,
    };

    obj.pin = data.pinNumber;

    if (data.deviceId) {
      obj.deviceId = data.deviceId;
    }
    obj.mfa = true;
    return this.webservice.post("authentication/validate-login-code", obj);
  }

  validateDOB(data) {
    const obj: any = {
      birthDate: data,
      session_token: this.userSession.sessionToken,
    };
    return this.webservice.post("authentication/validate-dob", obj);
  }

  callUpdatePhoneNumber(data) {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      phoneNumber: data,
    };
    return this.webservice.post(API_LIB.AUTHENTICATION_UPDATE_ACCOUNT, obj);
  }

  validateReferrerCode(bonusCode: string) {
    const obj: any = {
      referrer_code: bonusCode,
    };

    return this.webservice.get("authentication/validate-referrer-code", obj);
  }

  callResetPassword(data, customerid) {
    const obj: any = {
      customer_id: customerid,
      newpassword: data.passwordGroup.newPassword,
      secAnswer: data.securityGroup.securityAnswer,
      secAnswer2: data.securityGroup.secondSecurityAnswer,
      skin_code: "tc",
    };

    return this.webservice.post("authentication/reset-password", obj);
  }

  callChangePasswordByToken(data, customerid, resetPasswordToken) {

    const month =
      ~~data.dateOfBirthGroup.dateOfBirthMonth < 10
        ? "0" + data.dateOfBirthGroup.dateOfBirthMonth
        : data.dateOfBirthGroup.dateOfBirthMonth;

    const dob =
      data.dateOfBirthGroup.dateOfBirthYear +
      "-" +
      month +
      "-" +
      data.dateOfBirthGroup.dateOfBirthDay;

    const obj: any = {
      customer_id: customerid,
      newpassword: data.passwordGroup.newPassword,
      resetPasswordToken: resetPasswordToken,
      dob: dob
    };

    return this.webservice.post("authentication/reset-password", obj);
  }

  callResetPasswordDOBAndToken(data, email, token, customerId) {

    let month =
      ~~data.dateOfBirthGroup.dateOfBirthMonth < 10
        ? "0" + data.dateOfBirthGroup.dateOfBirthMonth
        : data.dateOfBirthGroup.dateOfBirthMonth;

    let dob =
      data.dateOfBirthGroup.dateOfBirthYear +
      "-" +
      month +
      "-" +
      data.dateOfBirthGroup.dateOfBirthDay;


    let obj: any = {
      email: email,
      customer_id: customerId,
      newpassword: data.passwordGroup.newPassword,
      dob: dob,
      token: token
    };

    return this.webservice.post("authentication/reset-password-by-token", obj);
  }

  callResetPasswordByToken(data) {
    const obj: any = {
      email: data.username,
      newpassword: data.passwordGroup.newPassword,
      token: data.token,
    };

    return this.webservice.post("authentication/reset-password-by-token", obj);
  }

  callForgotPasswordApi(email, clientApp) {
    const obj: any = {
      email: email,
      clientapp: clientApp
    };

    return this.webservice.post("authentication/forgot-password-api", obj);
  }

  callValidateToken(data) {
    const obj = {
      token: data,
    };
    return this.webservice.post("authentication/validate-token", obj);
  }

  callValidateTokenMFAEmail(data) {
    let obj = {
      email: data.email,
      token: data.token,
    };
    return this.webservice.post("authentication/validate-token", obj);
  }

  callResendPin(email: string, clientapp) {
    let obj: any = {
      email: email,
      clientapp: clientapp,
    };

    return this.webservice.post("authentication/resendPin", obj);
  }


  public callResendPinStrongAuth(customerId?) {
    const customer_id = (!this.customer) ? customerId : this.customer.customerId;

    const obj: any = {
      customer_id: customer_id,
      session_token: this.userSession.sessionToken
    }

    return this.webservice.post('customer/resend-pin', obj);
  }

  callResetPasswordStep1(data) {
    const obj: any = {
      email: data.email,
      pin: data.pin,
      clientapp: data.clientapp,
    };

    return this.webservice.post("authentication/reset-password-step1", obj);
  }

  callResetPasswordStep2(data) {
    // formatted DOB
    // add a 0 in front of the month
    const month =
      ~~data.dateOfBirthGroup.dateOfBirthMonth < 10
        ? "0" + data.dateOfBirthGroup.dateOfBirthMonth
        : data.dateOfBirthGroup.dateOfBirthMonth;
    const dob = new Date(
      data.dateOfBirthGroup.dateOfBirthYear +
      "-" +
      month +
      "-" +
      data.dateOfBirthGroup.dateOfBirthDay
    );

    const obj: any = {
      email: data.email,
      //ssn: data.ssn,
      birthDate: dob.toISOString().split("T")[0],
      clientapp: data.clientapp,
    };

    if (this.configService.getConfig().FEATURE_SIGNUP_SHOW_SSN_FIELD === '1') {
      obj.ssn = data.ssn;
    }

    return this.webservice.post("authentication/reset-password-step2", obj);
  }

  callResetPasswordByEmail(data) {
    const obj: any = {
      email: data.email,
      newpassword: data.passwordGroup.newPassword,
      pin: data.pin,
      clientapp: data.clientapp,
    };

    if (!!data.customerId) {
      obj.customer_id = data.customerId;
    }

    return this.webservice.post("authentication/reset-password-by-email", obj);
  }

  getIDNowUrl() {
    const obj: any = {
      session_token: this.userSession.sessionToken
    }
    return this.webservice.get('kyc/get-idscan-url', obj);
  }

  getIDNowResult() {
    const obj: any = {
      session_token: this.userSession.sessionToken
    }
    return this.webservice.get('kyc/get-idscan-result', obj);
  }

  callReconfirmCustomerDetails(user_info_updated) {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken,
      user_info_updated: user_info_updated
    }
    return this.webservice.post('authentication/reconfirm-customer-details', obj);
  }

  callShowQRCode() {
    const obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.sessionToken
    }
    return this.webservice.get('authentication/show-qr-code', obj);
  }
  checkMigratedPlayer() {
    if (this.customer.getCustomDataProperty('CRE_BY') === 'MIGRATION' || this.customer.getCustomDataProperty('CRE_BY') === 'RESORTS_MIGRATION') {
      return true;
    }
    return false;
  }

  callValidateSecureUploadDocumentToken(data) {
    let obj = {
      encrypted_customer_info_token: data
    };
    return this.webservice.post("authentication/validate-encrypted-customer-token", obj);
  }

}
